import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {
  getAdministrationCredentialsByEstablishment,
  getAttributionHoursByDegree,
  getAttributionHoursBySection,
  getAttributionHoursByTeacher,
  getAttributionLinesActiveForConnectedTeacher,
  getAttributionLinesByTeachers,
  getAttributions,
  getAttributionValidationDate,
  getClassesForSchoolReportType,
  getClassesWithClassLinks,
  getClassesWithNoonPeriodTime,
  getEstablishmentsDetails,
  getStudentAbsenceForEducatorByDayHourStudentId,
  getStudentAbsenceForTeacherByDayHourStudentId,
  getYearsOfStudy,
  selectAttributionYear,
  selectedProfile,
  selectIsCopyingNextYearAttributions,
  selectSchool,
  selectSchoolId,
  StoreState
} from '@redux/reducers';
import {combineLatest, EMPTY, Observable, of, throwError} from 'rxjs';
import {StaffMember} from '@model/staffMember';
import {StaffMemberService} from '@services/staffMember.service';
import {catchError, filter, map, mergeAll, switchMap, take} from 'rxjs/operators';
import {SchoolService} from '@services/school.service';
import {School} from '@model/school';
import {Degree} from '@model/degree';
import {Section} from '@model/section';
import {Class, ClassWithHorizonClassLink} from '@model/class';
import {Lesson} from '@model/lesson';
import {LessonService} from '@services/lesson.service';
import {AttributionService} from '@services/attribution.service';
import {Attribution} from '@model/attribution';
import {DateTime} from 'luxon';
import {AttributionHoursByDegree} from '@model/attribution-hours-by-degree';
import {AttributionHoursBySection} from '@model/attribution-hours-by-section';
import {AttributionHoursByTeacher} from '@model/attribution-hours-by-teacher';
import {AttributionLineTeacherDetail} from '@model/attribution-line-teacher-detail';
import {
  attributionHoursByDegreeAction,
  attributionHoursBySectionAction,
  attributionHoursByTeacherAction,
  attributionLinesForTeacherAction, 
  attributionsAction, 
  attributionValidationDateAction, 
  attributionYearAction, 
  lessonsAction, 
  nextYearAttributionsAction} from '@redux/actions/attributions.action';
import {
  ParentLoadedAction,
  ParentSearchResultLoadedAction,
  ParentSearchResultResetAction,
  ResetSelectedParentAction,
  StudentInClassLoadedAction
} from '@redux/actions/students.action';
import {Student} from '@model/student';
import {StudentService} from '@services/student.service';
import {Page} from '@model/page';
import {Parent} from '@model/parent';
import {ParentService} from '@services/parent.service';
import {SearchData} from '@model/search-data';
import {StudentDetail} from '@model/student-detail';
import {SchoolReportsService} from '@services/school-reports.service';
import {Profile} from '@model/profile';
import {StudentInAttribution} from '@model/student-in-attribution';
import {Schedule} from '@model/schedule';
import {AttributionTimeEntry} from '@model/attribution-time-entry';
import {StudentAbsence} from '@model/student-absence';
import {StudentInClass} from '@model/student-in-class';
import {
  studentAbsenceCancelled,
  studentAbsenceCreated,
  studentAbsencesByAttributionLoaded
} from '@redux/actions/student-absence.action';
import {schoolReportTypesLoaded, schoolReportTypeToClassAssignementLoaded} from '@redux/actions/school-report.action';
import {isConflictErrorWithIntegrityViolation, SharedFacadeService} from '@shared/shared-facade.service';
import {StorageService} from '@services/storage.service';
import {
  ClassNoonPeriodTimeDeleted,
  ClassNoonPeriodTimeLoaded,
  ClassNoonPeriodTimeSaved,
  ResetSelectedOptionAction,
  SchoolSectionOptionRemovedAction,
  SchoolSectionOptionSavedAction
} from '@redux/actions/school.action';
import {Option} from '@model/option';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DATE_DISPLAY_FORMAT, getDateTime} from '@shared/util/date-util';
import {StudentAbsenceQParams} from '@model/student-absence-q-params';
import {PredefinedTextContentService} from '@services/predefined-text-content.service';
import {predefinedTextContentDeleted} from '@redux/actions/predefined-text-content.action';
import {PredefinedTextContent, PredefinedTextContentMap} from '@model/predefined-text-content';
import {YearlySchoolDataType} from '@model/yearly-school-data-type';
import {SchoolSetting} from '@model/school-setting';
import {Resource} from '@model/resource';
import {ResourceService} from '@services/resource.service';
import {SchoolResourceYearlyReservation} from '@model/school-resource-yearly-reservation';
import {ClassNoonPeriodTime} from '@model/class-noon-period-time';
import {ClassWithClassNoonPeriodTime} from '@model/class-with-class-noon-period-time';
import {AttributionSummary} from '@model/attribution-summary';
import {YearOfStudy} from '@model/year-of-study';
import {establishmentLoaded, yearsOfStudyLoaded} from '@redux/actions/reference-data.action';
import {ReferenceDataService} from '@services/reference-data.service';
import {Establishment} from '@model/establishment';
import {HorizonClassLink} from '@model/horizon-class-link';
import {AdministrationCredentials, AdministrationCredentialsRequest} from '@model/administration-credentials';
import {HorizonClassLinkActions} from '@redux/actions/horizon-class-link.action';

@Injectable({
  providedIn: 'root'
})
export class AdminFacadeService {
  private ATTRIBUTION_YEAR_LOCAL_KEY = 'ATTRIBUTION_YEAR';
  private ATTRIBUTION_VALIDATION_DATE_LOCAL_KEY = 'ATTRIBUTION_VALIDATION_DATE';

  staffMembers$: Observable<StaffMember[]>;
  allStaffMembers$: Observable<StaffMember[]>;
  isCabangaLite$: Observable<boolean>;
  elementarySchool$: Observable<boolean>;

  school$: Observable<School>;
  degrees$: Observable<Degree[]>;
  sections$: Observable<Section[]>;
  classes$: Observable<Class[]>;
  schoolSettings$: Observable<SchoolSetting[]>;
  smsCommunicationDisabled$: Observable<boolean>;
  classesWithHorizonClassLinks$: Observable<ClassWithHorizonClassLink[]>;
  administrationCredentials$: Observable<AdministrationCredentials[]>;

  attributionYear$: Observable<number>;
  attributionValidationDate$: Observable<DateTime>;
  lessons$: Observable<Lesson[]>;
  subject$: Observable<string[]>;
  attributions$: Observable<Attribution[]>;
  attributionHoursByDegree$: Observable<AttributionHoursByDegree[]>;
  attributionHoursBySection$: Observable<AttributionHoursBySection[]>;
  attributionHoursByTeacher$: Observable<AttributionHoursByTeacher[]>;
  attributionsActivesForTeacher$: Observable<AttributionSummary[]>;
  attributionLinesByTeachers$: Observable<{ [id: number]: AttributionLineTeacherDetail[] }>;
  attributionLinesActivesForConnectedTeachers$: Observable<AttributionLineTeacherDetail[]>;

  registrationYear$: Observable<number>;
  activeSchoolYear$: Observable<number>;
  selectedStudent$: Observable<StudentDetail>;
  selectedParent$: Observable<Parent>;
  parentSearchResult$: Observable<Page<Parent>>;
  studentSearchResult$: Observable<Page<Student>>;
  profile$: Observable<Profile>;

  studentsByAttribution$: Observable<{ [id: number]: StudentInAttribution[]; }>;
  schedule$: Observable<Schedule>;
  teacherSchedule$: Observable<{[id: number]: {[day: string]: {[time: string]: AttributionTimeEntry}}}>;
  studentAbsencesForTeacherByDayHourStudentId$: Observable<{ [day: string]: { [hour: string]: {[studentId: number]: StudentAbsence} } }>;
  studentAbsencesForEducatorByDayHourStudentId$: Observable<{ [day: string]: { [hour: string]: {[studentId: number]: StudentAbsence} } }>;
  studentsInClass$: Observable<StudentInClass[]>;
  studentAbsencesByDayByHour$: Observable<any[]>;
  isMobile$: Observable<boolean>;
  schoolSectionOptions$: Observable<Option[]>;
  isCopingAttributionsNextYear$: Observable<boolean>;
  predefinedTextContentsMap$: Observable<PredefinedTextContentMap>;
  isAdminStaffMember$: Observable<boolean>;
  resources$: Observable<Resource[]>;
  schoolResourceYearlyReservations$: Observable<SchoolResourceYearlyReservation[]>;
  classesWithClassNoonPeriodTime$: Observable<ClassWithClassNoonPeriodTime[]>;
  establishmentsDetails$: Observable<Establishment[]>;
  yearsOfStudy$: Observable<YearOfStudy[]>;
  studentHorizonSynchronizationEnabled$: Observable<boolean>;
  staffMemberHorizonSynchronizationEnabled$: Observable<boolean>;

  constructor(private _store: Store<StoreState>,
              private _localStorage: StorageService,
              private _sharedFacade: SharedFacadeService,
              private _staffMemberService: StaffMemberService,
              private _lessonService: LessonService,
              private _schoolService: SchoolService,
              private _attributionService: AttributionService,
              private _studentService: StudentService,
              private _parentService: ParentService,
              private _schoolReportsService: SchoolReportsService,
              private _predefinedTextContentService: PredefinedTextContentService,
              private _resourceService: ResourceService,
              private _referenceDataService: ReferenceDataService,
              private _snackBar: MatSnackBar) {
    this.allStaffMembers$ = this._store.pipe(
      select(state => state.staffMembers.staffMembers)
    );
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.school$ = this._store.pipe(selectSchool);
    this.smsCommunicationDisabled$ = this.school$.pipe(
      map(school => school.smsCommunicationDisabled)
    );
    this.profile$ = this._store.pipe(selectedProfile);
    this.degrees$ = this._store.pipe(
      selectSchool,
      map(school => school.degrees)
    );
    this.sections$ = this._sharedFacade.sections$;
    this.classes$ = this._sharedFacade.classes$;
    this.schoolSettings$ = this._sharedFacade.schoolSettings$;
    this.attributionYear$ = this._store.pipe(selectAttributionYear);
    this.attributionValidationDate$ = this._store.pipe(
      select(getAttributionValidationDate)
    );
    this.attributions$ = this._store.pipe(
      select(getAttributions)
    );
    this.lessons$ = this._sharedFacade.lessons$;
    this.subject$ = this.lessons$.pipe(
      map(lessons => lessons.map(lesson => lesson.subject)),
      map(subjects => Array.from(new Set(subjects))),
    );
    this.attributionHoursByDegree$ = this._store.pipe(
      select(getAttributionHoursByDegree)
    );
    this.attributionHoursBySection$ = this._store.pipe(
      select(getAttributionHoursBySection)
    );
    this.attributionHoursByTeacher$ = this._store.pipe(
      select(getAttributionHoursByTeacher)
    );
    this.attributionsActivesForTeacher$ = this._sharedFacade.attributionsActivesForTeacher$;
    this.attributionLinesByTeachers$ = this._store.pipe(
      select(getAttributionLinesByTeachers)
    );
    this.attributionLinesActivesForConnectedTeachers$ = this._store.pipe(
      select(getAttributionLinesActiveForConnectedTeacher)
    );
    this.registrationYear$ = this._sharedFacade.registrationYear$;
    this.activeSchoolYear$ =  this._sharedFacade.activeSchoolYear$;
    this.selectedStudent$ = this._sharedFacade.selectedStudent$;
    this.selectedParent$ = this._store.pipe(
      select(state => state.students.parentSelected),
    );
    this.parentSearchResult$ = this._store.pipe(
      select(state => state.students.parentSearchResult)
    );
    this.studentSearchResult$ = this._store.pipe(
      select(state => state.students.studentSearchResult)
    );
    this.studentsInClass$ = this._store.pipe(
      select(state => state.students.studentsInClass)
    );
    this.studentsByAttribution$ = this._sharedFacade.studentsByAttribution$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.teacherSchedule$ = this._sharedFacade.teacherSchedule$;
    this.studentAbsencesForTeacherByDayHourStudentId$ = this._store.pipe(
      select(getStudentAbsenceForTeacherByDayHourStudentId)
    );
    this.studentAbsencesForEducatorByDayHourStudentId$ = this._store.pipe(
      select(getStudentAbsenceForEducatorByDayHourStudentId)
    );
    this.studentAbsencesByDayByHour$ = this._sharedFacade.studentAbsencesByDayByHour$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.schoolSectionOptions$ = this._sharedFacade.schoolSectionOptions$;
    this.isCopingAttributionsNextYear$ = this._store.pipe(selectIsCopyingNextYearAttributions);
    this.predefinedTextContentsMap$ = this._sharedFacade.predefinedTextContentsMap$;
    this.isAdminStaffMember$ = this._sharedFacade.isAdminStaffMember$;
    this.resources$ = this._sharedFacade.resources$;
    this.schoolResourceYearlyReservations$ = this._sharedFacade.schoolResourceYearlyReservations$;
    this.classesWithClassNoonPeriodTime$ = this._store.pipe(
      select(getClassesWithNoonPeriodTime)
    );
    this.establishmentsDetails$ = this._store.pipe(select(getEstablishmentsDetails));
    this.yearsOfStudy$ = this._store.pipe(select(getYearsOfStudy));
    this.isCabangaLite$ = this._sharedFacade.isCabangaLite$;
    this.elementarySchool$ = this.school$.pipe(
      map(school => !!school.elementarySchool)
    );

    this.classesWithHorizonClassLinks$ = this._store.pipe(select(getClassesWithClassLinks)) as Observable<ClassWithHorizonClassLink[]>;
    this.administrationCredentials$ = this._store.pipe(select(getAdministrationCredentialsByEstablishment));
    this.studentHorizonSynchronizationEnabled$ = this._sharedFacade.studentHorizonSynchronizationEnabled$;
    this.staffMemberHorizonSynchronizationEnabled$ = this.school$.pipe(
      map(school => school.staffMemberHorizonSynchronizationEnabled ?? false)
    );
  }

  refreshStaffMembers() {
    this._sharedFacade.refreshStaffMembers();
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  updateStaffMember(staffMember: StaffMember) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._staffMemberService.updateStaffMember(schoolId, staffMember))
    ).subscribe(() => this.refreshStaffMembers());
  }

  createStaffMember(staffMember: StaffMember) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._staffMemberService.createStaffMember(schoolId, staffMember))
    ).subscribe(() => this.refreshStaffMembers());
  }

  createStaffMembers(staffMembers: StaffMember[]): Observable<StaffMember> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(
        schoolId => staffMembers.map(staffMember => {
            return this._staffMemberService.createStaffMember(schoolId, staffMember).pipe(
              map(() => {
                return {};
              }), // return an empty object if success
              catchError(() => of(staffMember)) // return the failed staffmember if error
            );
          }
        )
      ),
      mergeAll(3)
    );
  }

  loadSchool() {
    this._sharedFacade.loadSchool();
  }

  saveDegree(degree: Degree) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.createDegree(schoolId, degree))
    ).subscribe(() => this.loadSchool());
  }

  deleteDegree(name: string) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.deleteDegree(schoolId, name))
    ).subscribe(() => this.loadSchool());
  }

  saveSection(section: Section) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.createSection(schoolId, section))
    ).subscribe(() => this.loadSchool());
  }

  deleteSection(name: string) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.deleteSection(schoolId, name))
    ).subscribe(() => this.loadSchool());
  }

  saveClass(clazz: Class, horizonClassLink?: HorizonClassLink) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.saveClass(schoolId, clazz))
    ).subscribe((classId) => {
      this.loadSchool();
      if (horizonClassLink && !horizonClassLink.horizonId) {
        this.saveHorizonClassLink({...horizonClassLink, classId});
      }
    });
  }

  loadHorizonClassLinks(forceReload = true) {
    this._sharedFacade.loadHorizonClassLinks(forceReload);
  }

  saveHorizonClassLink(horizonClassLink: HorizonClassLink) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        if (!horizonClassLink.id && horizonClassLink.establishmentFase) {
          return this._schoolService.postHorizonClassLink(school.id, {...horizonClassLink, schoolYear: school.activeSchoolYear});
        } else if (horizonClassLink.id && horizonClassLink.establishmentFase) {
          return this._schoolService.putHorizonClassLink(school.id, {...horizonClassLink, schoolYear: school.activeSchoolYear});
        } else if (horizonClassLink.id && !horizonClassLink.establishmentFase) {
          return this._schoolService.deleteHorizonClassLink(school.id, horizonClassLink.id);
        }
        return EMPTY;
      })
    ).subscribe(() => this.loadHorizonClassLinks());
  }

  loadAdministrationCredentials() {
    this._sharedFacade.loadAdministrationCredentials();
  }

  saveAdministrationCredentials(administrationCredentials: AdministrationCredentialsRequest) {
    this.school$.pipe(
      take(1),
      switchMap(school => administrationCredentials.id
        ? this._schoolService.putAdministrationCredentials(school.id, administrationCredentials.id, administrationCredentials)
        : this._schoolService.postAdministrationCredentials(school.id, administrationCredentials))
    ).subscribe(() => {
      this._snackBar.open('Les identifiants ont été enregistrés.', null, {duration: 3000});
      this.loadAdministrationCredentials();
    });
  }

  updateActiveSchoolYear(activeSchoolYear: number) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.updateActiveSchoolYear(schoolId, activeSchoolYear))
    ).subscribe(() => {
      this.loadSchool();
      this._store.dispatch(HorizonClassLinkActions.resetHorizonClassLinks());
    });
  }

  loadAttributionYear() {
    const attributionYearFromLocalStorage = this._localStorage.getItem<string>(this.ATTRIBUTION_YEAR_LOCAL_KEY);
    if (attributionYearFromLocalStorage) {
      this._store.dispatch(attributionYearAction.setAttributionYear({year: parseFloat(attributionYearFromLocalStorage)}));
    } else {
      this.school$.pipe(
        take(1),
        map(school => school.activeSchoolYear)
      ).subscribe(activeSchoolYear => this.updateAttributionYear(activeSchoolYear));
    }
  }

  updateAttributionYear(year: any) {
    this._localStorage.setItem(this.ATTRIBUTION_YEAR_LOCAL_KEY, year);
    this._store.dispatch(attributionYearAction.setAttributionYear({year: year}));
  }

  loadAttributionValidationDate() {
    const validationDate = this._localStorage.getItem<string>(this.ATTRIBUTION_VALIDATION_DATE_LOCAL_KEY);
    if (validationDate) {
      this._store.dispatch(attributionValidationDateAction.setAttributionValidationDate({date: DateTime.fromFormat(validationDate, DATE_DISPLAY_FORMAT)}));
    } else {
      const today = DateTime.now();
      this.updateAttributionValidationDate(today);
    }
  }

  updateAttributionValidationDate(date: DateTime) {
    this._localStorage.setItem(this.ATTRIBUTION_VALIDATION_DATE_LOCAL_KEY, date.toFormat('dd/MM/yyyy'));
    this._store.dispatch(attributionValidationDateAction.setAttributionValidationDate({date: date}));
  }

  loadLessonsForAttributionYear() {
    combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._lessonService.loadLessons(school.id, attributionYear))
    ).subscribe(lessons => this._store.dispatch(lessonsAction.setLessons({lessons: lessons})));
  }

  saveLesson(lesson: Lesson) {
    combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._lessonService.saveLesson(Object.assign({}, lesson, {year: attributionYear}), school.id))
    ).subscribe(_lesson => this._store.dispatch(lessonsAction.addLesson({lesson: _lesson})));
  }

  deleteLesson(id: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._lessonService.deleteLesson(id, school.id))
    ).subscribe(() => this._store.dispatch(lessonsAction.deleteLesson({lessonId: id})));
  }

  loadAttributions(sectionName: string) {
    combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._attributionService.loadAttributions(school.id, attributionYear, sectionName))
    ).subscribe(attributions => this._store.dispatch(attributionsAction.setAttributions({attributions: attributions})));
  }

  saveAttribution(attribution: Attribution) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.saveAttribution(school.id, attribution))
    ).subscribe(_attribution => this._store.dispatch(attributionsAction.addAttribution({attribution: _attribution})));
  }

  deleteAttribution(id: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.deleteAttribution(school.id, id))
    ).subscribe(() => this._store.dispatch(attributionsAction.deleteAttribution({attributionId: id})));
  }

  loadAttributionStatsByDegree() {
    return combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._attributionService.loadAttributionsStatsByDegrees(school.id, attributionYear))
    ).subscribe(attributionHoursByDegreeList => this._store.dispatch(attributionHoursByDegreeAction.setAttributionHoursByDegree({attributionsHoursByDegree: attributionHoursByDegreeList})));
  }

  loadAttributionStatsBySection() {
    return combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._attributionService.loadAttributionsStatsBySections(school.id, attributionYear))
    ).subscribe(attributionHoursBySectionList => this._store.dispatch(attributionHoursBySectionAction.setAttributionHoursBySection({attributionsHoursBySection: attributionHoursBySectionList})));
  }

  loadAttributionStatsByTeacher() {
    return combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._attributionService.loadAttributionsStatsByTeacher(school.id, attributionYear))
    ).subscribe(attributionHoursByTeacherList => this._store.dispatch(attributionHoursByTeacherAction.setAttributionHoursByTeacher({attributionsHoursByTeacher: attributionHoursByTeacherList})));
  }

  loadAttributionLinesForTeacher(teacherId: number) {
    return combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._attributionService.loadAttributionLinesForTeacher(school.id, attributionYear, teacherId))
    ).subscribe(attributionLinesForTeacher => this._store.dispatch(
      attributionLinesForTeacherAction.setAttributionLinesForTeacher({teacherId: teacherId, attributionLinesTeacherDetail: attributionLinesForTeacher})));
  }

  loadAttributionsActivesForTeacherAndActiveSchoolYear(teacherId: number) {
    this._sharedFacade.loadAttributionsActivesForTeacher(teacherId);
  }

  loadAttributionLinesForTeacherForActiveSchoolYear(teacherId: number) {
    return this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.loadAttributionLinesForTeacher(school.id, school.activeSchoolYear, teacherId))
    ).subscribe(attributionLinesForTeacher => this._store.dispatch(
    attributionLinesForTeacherAction.setAttributionLinesForTeacher({teacherId: teacherId, attributionLinesTeacherDetail: attributionLinesForTeacher})));
  }

  loadAttributionLinesForConnectedTeacherForActiveSchoolYear() {
    this._sharedFacade.loadAttributionLinesForConnectedTeacher();
  }

  loadRegistrationYear() {
    this._sharedFacade.loadRegistrationYear();
  }

  updateRegistrationYear(number: any) {
    this._sharedFacade.updateRegistrationYear(number);
  }

  checkStudentWithEmailExists(email: string): Observable<boolean> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._studentService.checkStudentWithEmailExists(schoolId, email)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  checkStudentWithInssExists(inss: string): Observable<boolean> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._studentService.checkStudentWithInssExists(schoolId, inss)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  checkStudentWithProEcoIdExists(proEcoId: number): Observable<boolean> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._studentService.checkStudentWithProEcoIdExists(schoolId, proEcoId)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  checkStudentWithAdministrationIdExists(administrationId: string): Observable<boolean> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._studentService.checkStudentWithAdministrationIdExists(schoolId, administrationId)),
      map(() => true),
      catchError(() => of(false))
    );
  }


  getParentByEmail(email: string): Observable<Parent> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.getParentByEmail(schoolId, email)),
    );
  }

  checkParentWithEmailExists(email: string): Observable<boolean> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.checkParentWithEmailExists(schoolId, email)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  resetSelectedStudent() {
    this._sharedFacade.resetSelectedStudent();
  }

  saveParent(parent: Parent) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.saveParent(schoolId, parent))
    ).subscribe(_parent => this._store.dispatch(new ParentLoadedAction(_parent)));
  }

  deleteParent(parentId: number): Observable<void> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.deleteParent(schoolId, parentId))
    );
  }

  saveParentForStudentCSVImport(parent: Parent): Observable<Parent> {
    return this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.saveParent(schoolId, parent))
    );
  }

  selectParent(parentId: number) {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.getParent(schoolId, parentId))
    ).subscribe(parent => this._store.dispatch(new ParentLoadedAction(parent)));
  }

  resetSelectedParent() {
    this._store.dispatch(new ResetSelectedParentAction());
  }

  loadChildrenForParent(parentId: number) {
    this._sharedFacade.loadChildren(parentId);
  }

  resetChildrenForParent() {
    this._sharedFacade.resetStudentSearchResult();
  }

  searchParents(searchData: SearchData): void {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._parentService.searchParents(schoolId, searchData))
    ).subscribe(parents => this._store.dispatch(new ParentSearchResultLoadedAction(parents)));
  }

  resetParentSearchResult() {
    this._store.dispatch(new ParentSearchResultResetAction());
  }

  loadClassView(classId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getClassDetail(school.id, classId))
    ).subscribe(classView => this._store.dispatch(new StudentInClassLoadedAction(classView.students)));
  }

  loadSchoolReportTypes() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadSchoolReportTypes(school.id))
    ).subscribe(schoolReportTypes => this._store.dispatch(schoolReportTypesLoaded({schoolReportTypes: schoolReportTypes})));
  }

  loadSchoolReportTypeToClassAssignments() {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolReportsService.loadSchoolReportTypeToClassAssignments(school.id))
    ).subscribe(assignments => this._store.dispatch(schoolReportTypeToClassAssignementLoaded({assignments: assignments})));
  }

  getClassesForSchoolReportFromClassId(classId: number) {
    return this._store.pipe(
      select(getClassesForSchoolReportType, {classId: classId})
    );
  }

  loadStudentsByAttribution(attributionId: number) {
    this._sharedFacade.loadStudentsByAttribution(attributionId);
  }

  assignStudentToAttribution(attributionId: number, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.assignStudentToAttribution(school.id, attributionId, studentId))
    ).subscribe(() => this.loadStudentsByAttribution(attributionId));
  }

  assignStudentsToAttribution(attributionId: number, studentIds: number[]) {
    this.school$.pipe(
      take(1),
      switchMap(school => studentIds.map(studentId => this._attributionService.assignStudentToAttribution(school.id, attributionId, studentId))),
      mergeAll(3)
    ).subscribe({
      complete: () => this.loadStudentsByAttribution(attributionId)
    });
  }

  removeStudentToAttribution(attributionId: number, studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.removeStudentToAttribution(school.id, attributionId, studentId))
    ).subscribe(() => this.loadStudentsByAttribution(attributionId));
  }

  removeStudentsToAttribution(attributionId: number, studentIds: number[]) {
    this.school$.pipe(
      take(1),
      switchMap(school => studentIds.map(studentId => this._attributionService.removeStudentToAttribution(school.id, attributionId, studentId))),
      mergeAll(3)
    ).subscribe({
      complete: () => this.loadStudentsByAttribution(attributionId)
    });
  }

  saveSchedule(schedule: Schedule) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.saveSchedule(school.id, school.activeSchoolYear, schedule))
    ).subscribe(() => this.loadSchedule());
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadScheduleForTeacher(teacherId: number) {
    this._sharedFacade.loadTeacherSchedule(teacherId);
  }

  saveAttributionTimeEntry(attributionTimeEntry: AttributionTimeEntry, teacherId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._staffMemberService.saveAttributionToTeacherSchedule(school.id, teacherId, attributionTimeEntry))
    ).subscribe(() => this.loadScheduleForTeacher(teacherId));
  }

  removeAttributionTimeEntry(attributionTimeEntry: AttributionTimeEntry, teacherId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._staffMemberService.removeAttributionToTeacherSchedule(school.id, teacherId, attributionTimeEntry))
    ).subscribe(() => this.loadScheduleForTeacher(teacherId));
  }

  loadStudentAbsenceByParams(params: StudentAbsenceQParams) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadStudentAbsenceByParams(school.id, params))
    ).subscribe(absences => this._store.dispatch(studentAbsencesByAttributionLoaded({studentAbsences: absences})));
  }

  loadStudentAbsencesForStudent(studentId: number, year?: number) {
    this._sharedFacade.loadStudentAbsencesForStudent(studentId, year);
  }

  addStudentAbsence(studentAbsence: StudentAbsence) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._studentService.addStudentAbsence(school.id, studentAbsence)),
      catchError(err => isConflictErrorWithIntegrityViolation(err) ? EMPTY : throwError(err))
    ).subscribe(id => this._store.dispatch(
      studentAbsenceCreated({studentAbsence: {...studentAbsence, id: id}})
    ));
  }

  cancelStudentAbsence(studentId: number, studentAbsenceId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.cancelStudentAbsence(school.id, studentId, studentAbsenceId))
    ).subscribe(() => this._store.dispatch(studentAbsenceCancelled({studentAbsenceId: studentAbsenceId})));
  }

  copyAttributionsFromActiveSchoolYear() {
    this._store.dispatch(nextYearAttributionsAction.copyingNextYearAttributions());
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.copyAttributionsFromActiveSchoolYear(school.id)),
    ).subscribe(() => {
      this._snackBar.open('Attributions en cours de copie. Cette opération peut prendre plusieurs minutes. Veuillez patienter.');
      this._store.dispatch(nextYearAttributionsAction.copiedNextYearAttributions());
    });
  }

  copyActivesSchoolYearData(yearlySchoolDataType: YearlySchoolDataType): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.copyActivesSchoolYearData(school.id, yearlySchoolDataType)),
    ).subscribe(() => this._snackBar.open('Copie en cours', null, {duration: 2000}));
  }

  loadSchoolSettings(): void {
    this._sharedFacade.loadSchoolSettings();
  }

  saveSchoolSetting(setting: SchoolSetting): void {
    this._sharedFacade.saveSchoolSetting(setting);
  }

  removeSchoolSetting(settingKey : string): void {
    this._store.pipe(
      selectSchoolId,
      take(1),
      switchMap(schoolId => this._schoolService.deleteSchoolSetting(schoolId, settingKey))
    ).subscribe(() => this._sharedFacade.forceLoadSchoolSettings());
  }

  assignColorToAttribution(attributionId: number, color: string) {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._assignColorToAttribution(profile.id, attributionId, color));
  }

  private _assignColorToAttribution(teacherId: number, attributionId: number, color: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._attributionService.assignColorToAttribution(school.id, attributionId, color))
    ).subscribe(() => this._store.dispatch(attributionLinesForTeacherAction.setAttributionLineColorForTeacher({teacherId: teacherId, attributionId: attributionId, color: color})));
  }

  loadSectionOptions(sectionName: string) {
    this._sharedFacade.loadSectionOptions(sectionName);
  }

  saveSectionOptions(option: Option) {
    combineLatest([this.school$, this.registrationYear$]).pipe(
      take(1),
      switchMap(([school, registrationYear]) => this._schoolService.saveOption(
        school.id, {...option, schoolId: school.id, schoolYear: registrationYear}
    ))).subscribe((id) => this._store.dispatch(new SchoolSectionOptionSavedAction({id: id, ...option})));
  }

  removeSectionOption(sectionName: string, optionId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeOption(school.id, sectionName, optionId))
    ).subscribe(() => this._store.dispatch(new SchoolSectionOptionRemovedAction(optionId)));
  }

  resetSelectedOption() {
    this._store.dispatch(new ResetSelectedOptionAction());
  }

  resetIndividualLearningPlanComments() {
    this._sharedFacade.resetPedagogicalFollowUpComments();
  }

  loadPredefinedTextContents() {
    this._sharedFacade.loadPredefinedTextContents();
  }

  savePredefinedTextContent(predefinedTextContent: PredefinedTextContent) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._predefinedTextContentService.savePredefinedTextContent(school.id, predefinedTextContent))
    ).subscribe((_predefinedTextContent) => {
      this._sharedFacade.refreshPredefinedTextContents();
    });
  }

  deletePredefinedTextContent(predefinedTextContentId: string) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._predefinedTextContentService.removePredefinedTextContent(school.id, predefinedTextContentId))
    ).subscribe(() => {
      this._store.dispatch( predefinedTextContentDeleted({predefinedTextContentId}));
    });
  }

  loadResources() {
    this._sharedFacade.loadResources();
  }

  loadSchoolResourceYearlyReservationsByStaffmember(staffMemberId: number) {
    this._sharedFacade.loadSchoolResourceYearlyReservationsByStaffmember(staffMemberId);
  }

  loadClassNoonPeriodTime() {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._schoolService.getClassNoonPeriodTime(school.id))
    ).subscribe((classNoonPeriodTime) => {
      this._store.dispatch(new ClassNoonPeriodTimeLoaded(classNoonPeriodTime));
    });
  }

  saveClassNoonPeriodTime(classNoonPeriodTime: ClassNoonPeriodTime) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._schoolService.saveClassNoonPeriodTime(school.id, classNoonPeriodTime))
    ).subscribe(() => {
      this._snackBar.open('Enregistrement effectué', null, {duration: 2000});
      this._store.dispatch(new ClassNoonPeriodTimeSaved(classNoonPeriodTime));
    });
  }

  removeClassNoonPeriodTime(classId: number) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._schoolService.removeClassNoonPeriodTime(school.id, classId))
    ).subscribe(() => {
      this._snackBar.open('Enregistrement effectué', null, {duration: 2000});
      this._store.dispatch(new ClassNoonPeriodTimeDeleted(classId));
    });
  }

  loadEstablishment(faseEstablishment: number) {
    this.establishmentsDetails$.pipe(
      take(1),
      filter(establishments => !establishments.some(establishment => establishment.fase === faseEstablishment)),
      switchMap(() => this.school$.pipe(
        take(1),
        switchMap((school) => this._referenceDataService.getEstablishment(school?.educationAuthority?.fase, faseEstablishment)),
      ))
    ).subscribe((establishment) => {
      this._store.dispatch(establishmentLoaded({establishment}));
    });
  }

  loadYearsOfStudy() {
    this._referenceDataService.getYearsOfStudy().subscribe((yearsOfStudy) => {
      this._store.dispatch(yearsOfStudyLoaded({yearsOfStudy}));
    });
  }
}
